import BasicBreadcrumbs from '../../../components/Breadcrumb/MuiBreadcrumb';
import { LineOfBusinessEnum } from '../../../constants/BusinessConstants';
import { Card, CardContent, Grid, Button, Typography, Link } from '@mui/material';
import LocalPrintshopRoundedIcon from '@mui/icons-material/LocalPrintshopRounded';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ItemLabel from '../../../components/ItemLabel/ItemLabel';
import { Box, Stack } from '@mui/system';
import { HTTPMethod, callApi as pdfRequest } from '../../../services/HttpService/HttpService';
import { OrdersApi } from '../../../constants/ApiConstants';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PatientAcknowledgementFeedbackType } from '../../../common/enums';
import withTitle from '../../../components/withTitle/withTitle';
import NoData from '../../../components/noData/noData';
import loader from "../../../../src/assets/loader.gif";
import Container from "@mui/material/Container";

const PatientAcknowledgementPDF = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportItems, setReportItems] = useState([]);

  const lineOfBusinessPath = LineOfBusinessEnum.patientsToMembers;
  const userId = useSelector(state => state.authState.userData?.data?.user_info?.user_id ?? 0);
  const companyId = useSelector(state => state.authState.userData?.data?.company_settings?.[0]?.company_id ?? 0);
  const orderId = location.state?.order_id || location?.pathname?.split("/").pop();
  const patientName = location.state?.patientDetailsName;
  const checkUrlIsOrder = location.pathname.includes("orders");
  const breadcrumbItems = checkUrlIsOrder
    ? [{ label: "Orders", link: "/orders" }, { label: orderId }]
    : [{ label: lineOfBusinessPath, link: `/${lineOfBusinessPath.toLowerCase()}` }, { label: patientName }];

  const fetchReportData = async () => {
    setIsLoadingData(true);
    const requestBody = { order_id: orderId, company_id: companyId, user_id: userId };
    const response = await pdfRequest(HTTPMethod.Post, OrdersApi.patientAcknowledgementCheckList, requestBody);
    if (response?.status_code === 200 && response?.data?.patient_acknowledgement_response) {
      const reportResponse = response.data.patient_acknowledgement_response;
      setReportData(reportResponse.patient_acknowledgement);
      setReportItems(reportResponse.patient_acknowledgement_report?.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)) ?? []);
    }
    setIsLoadingData(false);
  };

  const handleDetailsPage = () => {
    if (checkUrlIsOrder) {
      navigate(`/orders/order-details/${orderId}`);
    } else {
      const patientId = reportData?.patient_id || location.state?.patient_id || location?.pathname?.split('/').pop();
      navigate(`/${lineOfBusinessPath.toLowerCase()}/${LineOfBusinessEnum.patientToMember.toLowerCase()}-details/${patientId}`);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  const companyName = reportItems?.[0]?.company_name ?? '';
  const patientLabel = LineOfBusinessEnum.patientToMember;
  const educationObjectives = reportItems.filter(item => item.type === PatientAcknowledgementFeedbackType.EducationObjectives);
  const safetyObjectives = reportItems.filter(item => item.type === PatientAcknowledgementFeedbackType.SafetyObjectives);
  const riskAssessment = reportItems.filter(item => item.type === PatientAcknowledgementFeedbackType.RiskAssessmentPatientAcknowledgement);
  const equipmentPolicyItem = reportItems.find(item => item.type === PatientAcknowledgementFeedbackType.EquipmentPolicy);
  const homeEvaluation = reportItems.filter(item => item.type === PatientAcknowledgementFeedbackType.HomeEvaluation);
  const safetyAndOps = reportItems.filter(item => item.type === PatientAcknowledgementFeedbackType.SafetyAndOperationsAcknowledgment);

  const getVal = (arr, index) =>
    arr.length > index && arr[index]?.value ? arr[index].value : 'N/A';

  return (
    <div data-testid="PatientAcknowledgementPDF">
      <Container className="mainContainer">
        <BasicBreadcrumbs items={breadcrumbItems} />
        {(reportData || reportItems.length > 0) ? (
          <div className="print-section">
            <Stack direction="row" alignItems="center" sx={{ fontSize: 'var(--main-heading)', fontWeight: 600, '@media print': { mb: 0 } }}>
              <ArrowBackIosIcon sx={{ cursor: "pointer", '@media print': { display: 'none' } }} data-testid="backButton" onClick={handleDetailsPage} />
              <p><span>{t('Prescriptions.patientAcknowledgementBookletTitle').replace(/patient/gi, patientLabel)}</span></p>
            </Stack>

            {/* Header card */}
            <Card variant="outlined" sx={{ mb: 5, '@media print': { border: 'none', boxShadow: 'none' } }}>
              <CardContent sx={{ p: 4, '@media print': { p: 0 } }}>
                <Grid container>
                  <Grid item xs={12} lg={6}>
                    {companyName && <ItemLabel label="Company name" text={companyName} />}
                    <ItemLabel label={`${patientLabel} name`} text={reportData?.patient_name} />
                    {reportData?.patient_id && (
                      <ItemLabel
                        label={`${patientLabel} ID`}
                        text={
                          <Link component={RouterLink} to={`/${LineOfBusinessEnum.patientsToMembers.toLowerCase()}/${LineOfBusinessEnum.patientToMember.toLowerCase()}-details/${reportData?.patient_id}`} sx={{ color: 'var(--color-base)', textDecoration: 'underline', cursor: 'pointer' }}>{reportData?.patient_id}</Link>
                        }
                      />
                    )}
                    {orderId && (
                      <ItemLabel
                        label="Order ID"
                        text={
                          <Link component={RouterLink} to={`/orders/order-details/${orderId}`} sx={{ color: 'var(--color-base)', textDecoration: 'underline', cursor: 'pointer' }}>{orderId}</Link>
                        }
                      />
                    )}
                    <ItemLabel label="Completed on" text={reportData?.completed_on} />
                    <ItemLabel label="Technician" text={reportData?.technician_name} />
                  </Grid>
                  <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', mt: 'auto', mb: 2.5, justifyContent: 'flex-end' }}>
                    <Grid item sx={{ display: 'flex', justifyContent: 'flex-end', '@media screen and (max-width: 480px)': { mt: 2.5, justifyContent: 'flex-start' } }}>
                      <Button variant="contained" data-testid="printButton" className='primaryButton' onClick={() => window.print()}>
                        <LocalPrintshopRoundedIcon className='marginRightFive' />{t("Prescriptions.print")}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Typography component="p" sx={{ fontSize: 'var(--common-heading)', color: 'var(--color-dimgray)', fontWeight: 400, mt: 1, mb: 5 }}>
              {t('Prescriptions.patientAcknowledgementIntro1').replace(/patient/gi, patientLabel.toLowerCase())}
              <Box component="ul" sx={{ mt: 2, mr: 0, mb: 0, ml: 5, p: 0, '& li': { mb: 1 } }}>
                <li>{t('Prescriptions.bookletSectionQualityServiceGoals')}</li>
                <li>{t('Prescriptions.bookletSectionEmergencyPreparedness')}</li>
                <li>{t('Prescriptions.bookletSectionPatientCommunication').replace(/patient/gi, patientLabel)}</li>
                <li>{t('Prescriptions.bookletSectionScopeOfServices')}</li>
                <li>{t('Prescriptions.bookletSectionDeliveryAndServicePractices')}</li>
                <li>{t('Prescriptions.bookletSectionNoticeOfPrivacyPractices')}</li>
              </Box>
            </Typography>

            <Typography component="p" sx={{ fontSize: 'var(--common-heading)', color: 'var(--color-dimgray)', fontWeight: 400, mt: 1, mb: 5 }}>
              {t('Prescriptions.patientAcknowledgementIntro2')}
            </Typography>

            {/* Education Objectives */}
            {educationObjectives.length > 0 && (
              <Card variant="outlined" sx={{ mb: 5, '@media print': { border: 'none', boxShadow: 'none' } }}>
                <Box sx={{ bgcolor: 'var(--color-whitesmoke-100)', height: '50px', p: 2.5, color: 'var(--color-dimgray)', fontWeight: 400, display: 'flex', '@media print': { p: 0, height: '40px' } }}>
                  <Box sx={{ color: 'var(--color-black)', height: '50px', fontWeight: 600, fontSize: 'var(--secondary-heading)', mb: 1.25, '@media print': { fontSize: 'var(--secondary-heading)', fontWeight: 'bold' } }}>{t('Prescriptions.educationObjectives')}</Box>
                </Box>
                <CardContent sx={{ p: 4, '@media print': { p: 0 } }}>
                  <Grid container>
                    {educationObjectives.map(item => (
                      <Grid item xs={12} key={item?.feedback_id}>
                        <ItemLabel label={item?.question?.replace(/patient/gi, patientLabel)} text={item?.value || 'N/A'} />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Safety Objectives */}
            {(safetyObjectives.length > 0 || riskAssessment.length > 0) && (
              <Card variant="outlined" sx={{ mb: 5, '@media print': { border: 'none', boxShadow: 'none' } }}>
                <Box sx={{ bgcolor: 'var(--color-whitesmoke-100)', height: '50px', p: 2.5, color: 'var(--color-dimgray)', fontWeight: 400, display: 'flex', '@media print': { p: 0, height: '40px' } }}>
                  <Box sx={{ color: 'var(--color-black)', height: '50px', fontWeight: 600, fontSize: 'var(--secondary-heading)', mb: 1.25, '@media print': { fontSize: 'var(--secondary-heading)', fontWeight: 'bold' } }}>{t('Prescriptions.safetyObjectives')}</Box>
                </Box>
                <CardContent sx={{ p: 4, '@media print': { p: 0 } }}>
                  <Grid container>
                    {safetyObjectives.map(item => (
                      <Grid item xs={12} key={item?.feedback_id}>
                        <ItemLabel label={item?.question?.replace(/patient/gi, patientLabel)} text={item?.value || 'N/A'} />
                      </Grid>
                    ))}
                    {riskAssessment.length > 0 && (
                      <Grid item xs={12} sx={{ mt: 2.5 }}>
                        <Typography component="p" sx={{ fontSize: 'var(--common-heading)', color: 'var(--color-dimgray)', fontWeight: 400, my: 1 }}>{t('Prescriptions.riskAssessmentFalls').replace(/patient/gi, patientLabel)}</Typography>
                        <Box sx={{ ml: 5 }}>
                          <ItemLabel label="Yes" text={getVal(riskAssessment, 0)} />
                          <ItemLabel label="No, Parties not present" text={getVal(riskAssessment, 1)} />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Equipment Policy */}
            {equipmentPolicyItem && (
              <Card variant="outlined" sx={{ mb: 5, '@media print': { border: 'none', boxShadow: 'none' } }}>
                <Box sx={{ bgcolor: 'var(--color-whitesmoke-100)', height: '50px', p: 2.5, color: 'var(--color-dimgray)', fontWeight: 400, display: 'flex', '@media print': { p: 0, height: '40px' } }}>
                  <Box sx={{ color: 'var(--color-black)', height: '50px', fontWeight: 600, fontSize: 'var(--secondary-heading)', mb: 1.25, '@media print': { fontSize: 'var(--secondary-heading)', fontWeight: 'bold' } }}>{t('Prescriptions.equipmentPolicy')}</Box>
                </Box>
                <CardContent sx={{ p: 4, '@media print': { p: 0 } }}>
                  <Grid container>
                    <Grid item xs={12}>
                      <ItemLabel
                        label={`All ${companyName} equipment and related components are required to be returned upon leaving services:`}
                        text={equipmentPolicyItem?.value || 'N/A'}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Home Evaluation */}
            {homeEvaluation.length > 0 && (
              <Card variant="outlined" sx={{ mb: 5, '@media print': { border: 'none', boxShadow: 'none' } }}>
                <Box sx={{ bgcolor: 'var(--color-whitesmoke-100)', height: '50px', p: 2.5, color: 'var(--color-dimgray)', fontWeight: 400, display: 'flex', '@media print': { p: 0, height: '40px' } }}>
                  <Box sx={{ color: 'var(--color-black)', height: '50px', fontWeight: 600, fontSize: 'var(--secondary-heading)', mb: 1.25, '@media print': { fontSize: 'var(--secondary-heading)', fontWeight: 'bold' } }}>{t('Prescriptions.homeEvaluation')}</Box>
                </Box>
                <CardContent sx={{ p: 4, '@media print': { p: 0 } }}>
                  <Grid container>
                    {homeEvaluation.map(item => (
                      <Grid item xs={12} key={item?.feedback_id}>
                        <ItemLabel label={item?.question} text={item?.value || 'N/A'} />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Other Information */}
            <Card variant="outlined" sx={{ mb: 5, '@media print': { border: 'none', boxShadow: 'none' } }}>
              <Box sx={{ bgcolor: 'var(--color-whitesmoke-100)', height: '50px', p: 2.5, color: 'var(--color-dimgray)', fontWeight: 400, display: 'flex', '@media print': { p: 0, height: '40px' } }}>
                <Box sx={{ color: 'var(--color-black)', height: '50px', fontWeight: 600, fontSize: 'var(--secondary-heading)', mb: 1.25, '@media print': { fontSize: 'var(--secondary-heading)', fontWeight: 'bold' } }}>{t('Prescriptions.otherInformation')}</Box>
              </Box>
              <CardContent sx={{ p: 4, '@media print': { p: 0 } }}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography component="p" sx={{ fontSize: 'var(--common-heading)', color: 'var(--color-dimgray)', fontWeight: 400, my: 1 }}>{t('Prescriptions.givenExplanationsBy')}</Typography>
                    <ItemLabel label="Driver" text={reportData?.technician_name} />
                    <ItemLabel label="Date" text={reportData?.completed_on} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Patient Instruction Sheet(s) */}
            {safetyAndOps.length > 0 && (
              <Card variant="outlined" sx={{ mb: 5, '@media print': { border: 'none', boxShadow: 'none' } }}>
                <Typography component="p" sx={{ fontSize: 'var(--common-heading)', color: 'var(--color-dimgray)', fontWeight: 400, my: 1, p: 4, '@media print': { p: 0 } }}>{t('Prescriptions.patientInstructionSheetIntro').replace(/patient/gi, patientLabel.toLowerCase())}</Typography>
                <Box sx={{ bgcolor: 'var(--color-whitesmoke-100)', height: '50px', p: 2.5, color: 'var(--color-dimgray)', fontWeight: 400, display: 'flex', '@media print': { p: 0, height: '40px' } }}>
                  <Box sx={{ color: 'var(--color-black)', height: '50px', fontWeight: 600, fontSize: 'var(--secondary-heading)', mb: 1.25, '@media print': { fontSize: 'var(--secondary-heading)', fontWeight: 'bold' } }}>{t('Prescriptions.operationSafetyInstructionsFor')}</Box>
                </Box>
                <CardContent sx={{ p: 4, '@media print': { p: 0 } }}>
                  <Grid container>
                    {safetyAndOps.map(item => (
                      <Grid item xs={12} key={item?.feedback_id}>
                        <ItemLabel label={item?.question} text={item?.value || 'N/A'} />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Signature Section */}
            {reportData?.signature && (
              <Stack direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'flex-end', mt: 15, gap: 15, pageBreakInside: 'avoid' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box component="img" src={reportData.signature} alt="Signature" sx={{ height: '60px', mb: 2, display: 'block', '@media print': { height: '40px' } }} />
                  <Box sx={{ borderTop: '1px solid var(--color-black)', mt: 0, mx: 'auto', mb: 1, height: '1px', '@media print': { mb: 0.5 } }} />
                  <Typography sx={{ fontWeight: 'bold', mt: 1 }}>{patientLabel}{t("Prescriptions.caregiverSignature")}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600, fontSize: 'var(--common-heading)' }}>{reportData?.received_by}</Typography>
                  <Box sx={{ borderTop: '1px solid var(--color-black)', mt: 0, mx: 'auto', mb: 1, height: '1px', '@media print': { mb: 0.5 } }} />
                  <Typography sx={{ fontWeight: 'bold', mt: 1 }}>{patientLabel}{t("Prescriptions.caregiverPrintedName")}</Typography>
                </Box>
              </Stack>
            )}
          </div>
        ) : (
          <Box sx={{ mt: 7.5 }}>
            {isLoadingData && (
              <NoData
                message={isLoadingData && <img alt="Loading..." src={loader} />}
              />
            )}
          </Box>
        )}
      </Container>
    </div>
  );
};

export default withTitle(PatientAcknowledgementPDF, () => `Acknowledgement of Receipt of ${LineOfBusinessEnum.patientToMember} Booklet`);
